/**
 * svg_inline.ts — 저장된 로고(data URL)를 인라인 SVG 로 그리기 위한 해석·정화.
 *
 *   왜: <img src=data:…svg> 는 SVG 안의 currentColor 가 검정으로 고정된다. 인라인(<svg> 를 DOM 에 직접)으로 넣으면
 *   글자색이 놓인 자리의 CSS color 를 따라가 흰 배경·어두운 배경을 한 파일로 쓸 수 있다(BrandLogo).
 *   정화: 업로드는 admin 만 하지만 스크립트·이벤트 속성·외부 링크·foreignObject 는 제거한다.
 */
export interface InlineSvg {
  /** 정화된 <svg …>…</svg> 문자열 */
  markup: string;
  /** viewBox 가로/세로 비율(없으면 null) — 컨테이너 aspect-ratio 에 쓴다 */
  ratio: number | null;
  /** 글자색이 currentColor 를 쓰는지 — 브랜드 화면 안내용 */
  usesCurrentColor: boolean;
}

const SVG_PREFIX = "data:image/svg+xml";

export function isSvgDataUrl(src: string): boolean {
  return src.startsWith(SVG_PREFIX);
}

function decodeDataUrl(src: string): string {
  const comma = src.indexOf(",");
  if (comma < 0) return "";
  const meta = src.slice(0, comma);
  const payload = src.slice(comma + 1);
  if (/;base64/i.test(meta)) {
    const bin = atob(payload);
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  }
  return decodeURIComponent(payload);
}

const DROP_TAGS = new Set(["script", "foreignObject", "iframe", "object", "embed"]);

function sanitize(root: Element): void {
  const walk = (el: Element) => {
    for (const child of Array.from(el.children)) {
      if (DROP_TAGS.has(child.localName)) {
        child.remove();
        continue;
      }
      for (const attr of Array.from(child.attributes)) {
        const name = attr.name.toLowerCase();
        const val = attr.value.trim().toLowerCase();
        if (name.startsWith("on")) child.removeAttribute(attr.name);
        else if ((name === "href" || name === "xlink:href") && !val.startsWith("#") && !val.startsWith("data:image/"))
          child.removeAttribute(attr.name);
      }
      walk(child);
    }
  };
  walk(root);
  for (const attr of Array.from(root.attributes)) if (attr.name.toLowerCase().startsWith("on")) root.removeAttribute(attr.name);
}

/**
 * <style> 안의 단순 클래스 규칙(`.st1 { fill: #333; }` — Illustrator 내보내기 형태)을 각 요소의 style 속성으로 옮기고
 * <style> 을 제거한다. 인라인 SVG 의 <style> 은 문서 전체에 적용되므로 그대로 두면 다른 화면 요소에 샐 수 있다.
 * 클래스 외 선택자(태그·id)는 드물어 지원하지 않고, 남으면 <style> 째로 제거한다(그 부분만 기본색으로 보임).
 */
function inlineStyleRules(root: Element): void {
  for (const styleEl of Array.from(root.querySelectorAll("style"))) {
    const css = (styleEl.textContent || "").replace(/\/\*[\s\S]*?\*\//g, "");
    const re = /\.([A-Za-z_][\w-]*)\s*\{([^}]*)\}/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(css))) {
      const decl = m[2].trim().replace(/\s+/g, " ");
      if (!decl) continue;
      for (const el of Array.from(root.querySelectorAll(`.${m[1]}`))) {
        const prev = el.getAttribute("style");
        el.setAttribute("style", prev ? `${decl.replace(/;?$/, ";")} ${prev}` : decl);
      }
    }
    styleEl.remove();
  }
}

const cache = new Map<string, InlineSvg | null>();

/** data URL → 인라인용 SVG. SVG 가 아니거나 해석 실패면 null(호출부는 <img> 로 폴백) */
export function parseInlineSvg(src: string): InlineSvg | null {
  if (!isSvgDataUrl(src)) return null;
  const hit = cache.get(src);
  if (hit !== undefined) return hit;
  let out: InlineSvg | null = null;
  try {
    const text = decodeDataUrl(src);
    const doc = new DOMParser().parseFromString(text, "image/svg+xml");
    const root = doc.documentElement;
    if (root.localName === "svg" && !doc.querySelector("parsererror")) {
      sanitize(root);
      inlineStyleRules(root);
      // 크기는 컨테이너가 정한다 — 고정 width/height 는 지우고 viewBox 만 남긴다
      root.removeAttribute("width");
      root.removeAttribute("height");
      root.setAttribute("preserveAspectRatio", root.getAttribute("preserveAspectRatio") || "xMidYMid meet");
      const vb = (root.getAttribute("viewBox") || "").split(/[\s,]+/).map(Number);
      const ratio = vb.length === 4 && vb[2] > 0 && vb[3] > 0 ? vb[2] / vb[3] : null;
      out = { markup: new XMLSerializer().serializeToString(root), ratio, usesCurrentColor: /currentColor/i.test(text) };
    }
  } catch {
    out = null;
  }
  cache.set(src, out);
  return out;
}
